import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import api from "api/api";
import endpoint from "api/endpoints";
import { ProcessingOverlay } from "common";
import ToastNotification from "common/toast/Toast";
import {
  buildIdempotencyKey,
  calculateNextRunDate,
  duplicateOrderMessage,
  formatPrice,
} from "config/config";
import { getAddressDisplayFields } from "utils/addressUtils";

import CheckoutSectionCard from "./CheckoutSectionCard";
import StripeCheckout from "./StripeCheckout";

import {
  EMAIL_ORDER,
  INVENTORY_LOCATION,
  INVENTORY_SUBSIDIARY,
  LEAD_SOURCE,
  SFC_FREE_SHIPPING,
  SHIPPING_COUNTRY,
  SHIPPING_DEFAULT,
} from "@/constants/constant";
import { Button, Icon } from "@/design-system";
import { removeSelectedItems } from "@/redux/slices/cartSlice";
import { clearOrderAgain } from "@/redux/slices/orderAgainSlice";

/** One address recap, in the reference's address-box dress (location_on beside
 *  the block, `neutral-50` fill), inside a titled section card. */
function AddressDisplayBlock({ title, address }) {
  const fields = getAddressDisplayFields(address);
  if (!fields) return null;
  const { name, streetLines, cityStateZip, countryDisplay, phone } = fields;
  return (
    <CheckoutSectionCard title={title}>
      <div className="flex items-start gap-[14px] rounded-md border border-line-subtle bg-[var(--neutral-50)] px-[var(--space-4)] py-[14px]">
        <Icon
          name="location_on"
          size={22}
          className="mt-[2px] shrink-0 text-blue-600"
        />
        <div className="min-w-0 flex-1 text-sm">
          {name && <div className="font-semibold">{name}</div>}
          {streetLines && (
            <div className="whitespace-pre-line text-ink-2">{streetLines}</div>
          )}
          {cityStateZip && <div className="text-ink-2">{cityStateZip}</div>}
          {countryDisplay && <div className="text-ink-2">{countryDisplay}</div>}
          {phone && <div className="text-ink-2">{phone}</div>}
        </div>
      </div>
    </CheckoutSectionCard>
  );
}

export default function CheckoutReview({
  stripeCustomerId,
  orderTotal,
  isProcessing,
  setIsProcessing,
  orderNote,
  cart = [],
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const userInfo = useSelector((state) => state.user.info);
  // Remove the cartItems selector since we're using the cart prop
  // Set by the payment step's "Billing address is the same as the delivery
  // address" checkbox (sessionStorage.billingSameAsShipping); display only here —
  // the billing address itself is resolved in the restore effect below.
  const [billingSameAsShipping, setBillingSameAsShipping] = useState(false);
  const [billingAddress, setBillingAddress] = useState(null);
  const [shippingAddress, setShippingAddress] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("invoice");
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState(null);

  useEffect(() => {
    // Get shipping addresses
    const storedShippingAddresses = JSON.parse(
      localStorage.getItem("checkoutShippingAddresses") || "[]"
    );
    const selectedShippingId = Number(
      localStorage.getItem("selectedShippingAddressId")
    );
    const selectedShippingAddress = storedShippingAddresses.find(
      (addr) => addr.id === selectedShippingId
    );
    setShippingAddress(selectedShippingAddress);

    // Get billing addresses
    const storedBillingAddresses = JSON.parse(
      localStorage.getItem("checkoutBillingAddresses") || "[]"
    );
    const selectedBillingId = Number(
      localStorage.getItem("selectedBillingAddressId")
    );
    const selectedBillingAddress = storedBillingAddresses.find(
      (addr) => addr.id === selectedBillingId
    );

    // "Billing address is the same as the delivery address" (payment step)
    // wins; otherwise the chosen billing address, falling back to shipping.
    // The key exists only after an explicit tick/untick; absent, apply the
    // payment step's default (ON when the account has no billing address).
    const storedSameAsShipping = sessionStorage.getItem(
      "billingSameAsShipping"
    );
    const sameAsShipping =
      storedSameAsShipping === null
        ? storedBillingAddresses.length === 0
        : storedSameAsShipping === "true";
    setBillingSameAsShipping(sameAsShipping);
    setBillingAddress(
      sameAsShipping
        ? selectedShippingAddress
        : selectedBillingAddress || selectedShippingAddress
    );

    // Get payment method and selected payment method ID from localStorage
    const storedPaymentMethod = localStorage.getItem("paymentMethod");
    const storedPaymentMethodId = localStorage.getItem(
      "selectedPaymentMethodId"
    );

    if (storedPaymentMethod) {
      setPaymentMethod(storedPaymentMethod);
    }
    if (storedPaymentMethodId) {
      setSelectedPaymentMethodId(storedPaymentMethodId);
    }
  }, []);

  // Build order payload for NetSuite integration
  const buildOrderPayload = () => {
    // Use cart directly since it's already filtered in CheckoutPage
    const selectedItems = cart;

    // Validate required data
    if (!userInfo?.id || !selectedItems?.length || !billingAddress) {
      return null;
    }

    // Helper function to check if user has special category
    const categorySFC = userInfo.category === 5 || userInfo.category === "5";

    // Create memo based on order note only
    const shippingnotes = orderNote || "";

    const orderPayload = {
      entity: {
        id: userInfo.id,
        // type: userInfo.searchstage,
      },
      item: {
        items: selectedItems.map((item) => {
          // const allocation = calculateQuantityAllocation(item);

          return {
            item: {
              id: item.id,
            },
            quantity: Number(item.quantity),
            // quantityCommitted: allocation.quantityCommitted,
            // quantityBackOrdered: allocation.quantityBackOrdered,
            custcol27: { id: LEAD_SOURCE },
            inventorysubsidiary: INVENTORY_SUBSIDIARY,
            inventorylocation: INVENTORY_LOCATION,
          };
        }),
      },
      // Invoice orders email at submit. Card orders are held server-side and
      // emailed by stripe.payment.service.js only after Stripe captures.
      toBeEmailed: paymentMethod === "invoice",
      email: categorySFC
        ? userInfo.custentity1 && userInfo.custentity1 !== userInfo.email
          ? `${userInfo.email}, ${userInfo.custentity1}, ${EMAIL_ORDER}`
          : `${userInfo.email}, ${EMAIL_ORDER}`
        : userInfo.email,
      shipMethod: {
        id: categorySFC ? SFC_FREE_SHIPPING : SHIPPING_DEFAULT,
      },
      leadsource: { id: LEAD_SOURCE },
      billingAddress: {
        addr1: billingAddress.address,
        addrText: billingAddress.addrText || "",
        city: billingAddress.city,
        country: { id: SHIPPING_COUNTRY },
        state: billingAddress.state,
        zip: billingAddress.zip,
      },
      shippingAddress: shippingAddress
        ? {
            addr1: shippingAddress.address,
            addrText: shippingAddress.addrText || "",
            addressee: shippingAddress.addrText
              ? shippingAddress.addrText.split("\n")[0]
              : "",
            city: shippingAddress.city,
            country: { id: SHIPPING_COUNTRY },
            state: shippingAddress.state,
            zip: shippingAddress.zip,
          }
        : {
            addr1: billingAddress.address,
            addrText: billingAddress.addrText || "",
            addressee: billingAddress.addrText
              ? billingAddress.addrText.split("\n")[0]
              : "",
            city: billingAddress.city,
            country: { id: SHIPPING_COUNTRY },
            state: billingAddress.state,
            zip: billingAddress.zip,
          },
      paymentMethod: paymentMethod,
      custbodydnd_shippingnotes: shippingnotes,
      // (Not requested to send the note to backend; keeping UI-only per your ask)
    };

    // Build recurring order payloads for subscription items (only selected ones)
    const subscriptionItems = selectedItems.filter(
      (item) =>
        item.custrecord_ci_sub_enabled &&
        item.custrecord_ci_sub_interval &&
        item.custrecord_ci_sub_unit
    );

    const recurringOrderPayload = subscriptionItems.map((item) => {
      const nextRunDate = calculateNextRunDate(
        item.custrecord_ci_sub_interval,
        item.custrecord_ci_sub_unit
      );

      return {
        name: `every ${item.custrecord_ci_sub_interval} ${item.custrecord_ci_sub_unit}`,
        custrecord_ro_customer: { id: userInfo.id },
        custrecord_ro_item: { id: item.id },
        custrecord_ro_quantity: Number(item.quantity),
        custrecord_ro_interval: Number(item.custrecord_ci_sub_interval),
        custrecord_ro_interval_unit: { id: "2" },
        custrecord_ro_next_run: nextRunDate,
        custrecord_ro_status: { id: "1" },
        // custrecord_prefer_delivery: {
        //   items: (item.subscriptionPreferredDeliveryDays || []).map(
        //     (dayNum) => ({ id: dayNum })
        //   ),
        // },
      };
    });

    return {
      orderPayload,
      recurringOrderPayload,
    };
  };

  // Stripe Payment Functions
  const createPaymentIntent = async (selectedPaymentMethodId) => {
    if (!selectedPaymentMethodId || !orderTotal) return;

    // Use cart directly since it's already filtered in CheckoutPage
    const selectedItems = cart;

    try {
      const token = await getAccessTokenSilently();
      const response = await api.post(
        endpoint.POST_CREATE_PAYMENT(),
        {
          paymentMethod: selectedPaymentMethodId,
          amount: formatPrice(orderTotal),
          currency: "cad",
          customerId: stripeCustomerId,
          description: `Order payment for ${selectedItems.length} item(s)`,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return response.data.paymentIntent;
    } catch (err) {
      throw new Error(
        err.response?.data?.message || "Failed to create payment intent"
      );
    }
  };

  const handlePaymentSuccess = (paymentResult) => {
    // Handle successful payment
    localStorage.removeItem("selectedPaymentMethodId");
    localStorage.removeItem("paymentMethod");
    localStorage.removeItem("pendingPaymentIntent");

    // Clear only selected items from cart
    dispatch(removeSelectedItems(userInfo?.id));
    // Order Again's Redux copy goes stale on a new order, clear it here so it
    // doesn't serve pre-checkout data until its staleness window expires.
    dispatch(clearOrderAgain());

    // Clear checkout addresses from localStorage
    localStorage.removeItem("checkoutShippingAddresses");
    localStorage.removeItem("selectedShippingAddressId");
    localStorage.removeItem("checkoutBillingAddresses");
    localStorage.removeItem("selectedBillingAddressId");
    sessionStorage.removeItem("billingSameAsShipping");

    navigate("/purchase-history", {
      state: {
        paymentIntent: paymentResult,
        orderAmount: orderTotal,
        paymentSuccessMessage: "Payment completed successfully!",
        fromCheckout: true,
      },
    });
  };

  const handlePaymentError = (error) => {
    ToastNotification.error(
      error.message || "Payment failed. Please try again."
    );
  };

  // The one blocking overlay for the whole checkout (CheckoutPage used to
  // render a second copy off the same `isProcessing`, with card wording, so an
  // on-account order showed "processing payment" — Oct 2026). On account never
  // touches Stripe, so its copy must not mention payment.
  const processingOverlay = (
    <ProcessingOverlay
      open={isProcessing}
      text={
        paymentMethod === "card"
          ? "Processing payment and creating your order..."
          : "Creating your order..."
      }
      detail={
        paymentMethod === "card"
          ? "We're securely processing your payment and setting up your order. This may take a few minutes."
          : "We're creating your order and sending confirmation details. Nothing is charged today — you'll be invoiced on your account terms."
      }
      redirectNote="You'll be redirected to your order history once complete."
    />
  );

  // If payment method is card, show StripeCheckout instead
  if (paymentMethod === "card") {
    return (
      <>
        {processingOverlay}
        <StripeCheckout
          stripeCustomerId={stripeCustomerId}
          orderTotal={orderTotal}
          createPaymentIntent={createPaymentIntent}
          handlePaymentSuccess={handlePaymentSuccess}
          handlePaymentError={handlePaymentError}
          buildOrderPayload={buildOrderPayload}
          isProcessing={isProcessing}
          setIsProcessing={setIsProcessing}
          userInfo={userInfo}
          billingAddress={billingAddress}
          selectedItems={cart}
        />
      </>
    );
  }

  const handlePlaceOrder = async () => {
    // Check if user ID is available
    if (!userInfo?.id) {
      ToastNotification.error(
        "User information not available. Please refresh the page and try again."
      );
      return;
    }

    // Check if billing address is available
    if (!billingAddress) {
      ToastNotification.error(
        "Please select a billing address before placing the order."
      );
      return;
    }

    // Check if cart has items
    const selectedItems = cart;
    if (!cart || cart.length === 0) {
      ToastNotification.error(
        "Your cart is empty. Please add items before placing an order."
      );
      return;
    }

    if (!selectedItems || selectedItems.length === 0) {
      ToastNotification.error(
        "No items available for checkout. Please select items to order."
      );
      return;
    }

    // Build order payload using the standardized function
    const payloadResult = buildOrderPayload();
    if (!payloadResult) {
      ToastNotification.error(
        "Unable to build order. Please check your information and try again."
      );
      return;
    }

    const { orderPayload, recurringOrderPayload } = payloadResult;

    try {
      setIsProcessing(true);
      const token = await getAccessTokenSilently();

      // Build deterministic idempotency key matching backend window logic
      const categorySFC = userInfo.category === 5 || userInfo.category === "5";
      const shipMethodId = categorySFC ? SFC_FREE_SHIPPING : SHIPPING_DEFAULT;

      const idemKey = buildIdempotencyKey(
        userInfo?.id,
        selectedItems, // Use only selected items for idempotency key
        userInfo?.category,
        shipMethodId
      );

      await api.post(endpoint.POST_SALES_ORDER(), orderPayload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Idempotency-Key": idemKey,
        },
      });

      // Order created successfully
      ToastNotification.success("Order placed successfully!");

      // Create recurring orders for subscription items
      if (recurringOrderPayload.length > 0) {
        for (const recurringOrder of recurringOrderPayload) {
          try {
            await api.post(endpoint.POST_RECURRING_ORDER(), recurringOrder, {
              headers: { Authorization: `Bearer ${token}` },
            });
          } catch (recurringError) {
            // ignore
          }
        }

        if (recurringOrderPayload.length > 0) {
          ToastNotification.success(
            `Subscription set up for ${recurringOrderPayload.length} item(s)!`
          );
        }
      }

      // Clear only selected items from cart after successful order
      dispatch(removeSelectedItems(userInfo?.id));
      // Order Again's Redux copy goes stale on a new order; clear it here so it
      // doesn't serve pre-checkout data until its staleness window expires.
      dispatch(clearOrderAgain());

      // Clear checkout data from localStorage
      localStorage.removeItem("checkoutShippingAddresses");
      localStorage.removeItem("selectedShippingAddressId");
      localStorage.removeItem("checkoutBillingAddresses");
      localStorage.removeItem("selectedBillingAddressId");
      sessionStorage.removeItem("billingSameAsShipping");
      localStorage.removeItem("selectedPaymentMethodId");
      localStorage.removeItem("paymentMethod");
      localStorage.removeItem("pendingPaymentIntent");

      // Redirect to order history
      navigate("/purchase-history", {
        state: { fromCheckout: true },
      });
    } catch (error) {
      const statusCode = error.response?.status;

      // 503/504 means the proxy timed out — we don't know if the order
      // was created or not. Keep the cart intact so items aren't lost if
      // the order failed. The idempotency key will prevent duplicates if
      // the user retries.
      if (statusCode === 503 || statusCode === 504) {
        // eslint-disable-next-line no-console
        console.warn(
          "Order request timed out (503/504). Order status unknown."
        );

        ToastNotification.warning(
          "The request timed out. Your order may have been placed — please check your order history before trying again.",
          { autoClose: false }
        );
        return;
      }

      const errorData = error.response?.data;
      const errorDetail =
        errorData?.error ||
        errorData?.message ||
        error.message ||
        "Failed to place order. Please try again.";
      const errorDetails = errorData?.errorDetails;

      let enhancedError;

      // Check if error mentions duplicate order (NetSuite externalId uniqueness)
      if (errorDetail.match(/This record already exists/i)) {
        enhancedError = duplicateOrderMessage();
      } else if (
        errorDetail.match(
          /Invalid Field Value (\d+) for the following field: item/i
        )
      ) {
        // Check if error mentions "Invalid Field Value" with an item ID
        const itemErrorMatch = errorDetail.match(
          /Invalid Field Value (\d+) for the following field: item/i
        );
        const invalidItemId = itemErrorMatch[1];

        // Find the item in cart by ID
        const invalidItem = cart.find(
          (item) => String(item.id) === invalidItemId
        );

        if (invalidItem) {
          const itemName = invalidItem.itemid || `Item ${invalidItemId}`;
          enhancedError = `Cannot process order: "${itemName}" is invalid or no longer available. Please remove it from your cart and try again.`;
        } else {
          enhancedError = `Cannot process order: Item with ID ${invalidItemId} is invalid or no longer available. Please review your cart and try again.`;
        }
      } else {
        enhancedError = errorDetail;
      }

      // Log full error details for debugging
      if (errorDetails) {
        // eslint-disable-next-line no-console
        console.error("NetSuite order error details:", errorDetails);
      }

      ToastNotification.error(enhancedError, { autoClose: false });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="space-y-6 relative">
      {processingOverlay}

      <div className="flex flex-col gap-[var(--space-5)]">
        {/* Shipping Address — always shown; the old display-only "Shipping
            address same as billing address" checkbox that hid it (and changed
            nothing in the order) is gone. The real choice lives in the payment
            step and is reflected in the Bill to block below. */}
        {shippingAddress && (
          <AddressDisplayBlock title="Ship to" address={shippingAddress} />
        )}

        {/* Billing Address */}
        {billingSameAsShipping ? (
          <CheckoutSectionCard title="Bill to">
            <div className="flex items-center gap-[14px] rounded-md border border-line-subtle bg-[var(--neutral-50)] px-[var(--space-4)] py-[14px] text-sm text-ink-2">
              <Icon
                name="location_on"
                size={22}
                className="shrink-0 text-blue-600"
              />
              Same as the delivery address above.
            </div>
          </CheckoutSectionCard>
        ) : (
          billingAddress && (
            <AddressDisplayBlock title="Bill to" address={billingAddress} />
          )
        )}

        {/* Payment Method Display */}
        <CheckoutSectionCard title="Payment">
          {paymentMethod === "invoice" ? (
            <div className="flex items-center gap-[var(--space-3)] rounded-md bg-success-50 px-[var(--space-4)] py-[14px]">
              <Icon
                name="verified"
                size={22}
                fill
                className="shrink-0 text-success-600"
              />
              <div className="text-sm text-success-700">
                <b>On account.</b> An invoice will be emailed after order
                confirmation — nothing due today.
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-[var(--space-3)] rounded-md border border-line-subtle bg-[var(--neutral-50)] px-[var(--space-4)] py-[14px]">
              <Icon
                name="credit_card"
                size={22}
                className="shrink-0 text-blue-600"
              />
              <div className="text-sm text-ink-2">
                <div className="font-semibold text-ink">Credit card</div>
                {selectedPaymentMethodId
                  ? `Payment method selected (ID: ${selectedPaymentMethodId.slice(
                      -4
                    )})`
                  : "Card payment method selected"}
              </div>
            </div>
          )}
        </CheckoutSectionCard>

        {/* Shipping note recap */}
        {orderNote && (
          <CheckoutSectionCard title="Shipping note">
            <div className="whitespace-pre-wrap text-sm text-ink-2">
              {orderNote}
            </div>
          </CheckoutSectionCard>
        )}

        {/* Buttons */}
        <div className="flex justify-between">
          <Button
            variant="outline"
            startIcon="arrow_back"
            onClick={() => navigate("/checkout/payment")}
            disabled={isProcessing}
          >
            Back
          </Button>
          <Button
            size="lg"
            startIcon="lock"
            onClick={handlePlaceOrder}
            disabled={isProcessing}
          >
            {isProcessing ? "Placing order…" : "Place order"}
          </Button>
        </div>
      </div>
    </div>
  );
}
